/**
 * Pure view logic for the building assistant panel: turn order, pending state, chat list order.
 */
import { formatDateForStorage } from './DateUtils';
import {
    ASSISTANT_TURN_TIMEOUT_SECONDS,
    type AssistantChat,
    type AssistantToolCall,
    type AssistantTurn,
} from '../types/assistant';

export interface TurnView extends AssistantTurn {
    key: string;
}

export type PendingView =
    | { state: 'none' }
    | { state: 'running'; question: string; steps: string[]; text: string }
    /** Older than the function timeout: the callable died without recording a turn. */
    | { state: 'failed'; question: string };

export interface ChatListItem {
    key: string;
    title: string;
    updatedAt: string;
    contextBuildingKey?: string;
}

/** RTDB returns arrays as arrays or index-keyed maps; normalise to an ordered array. */
export function toOrderedArray<T>(value: T[] | Record<string, T> | undefined): T[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter((v) => v !== undefined && v !== null);
    return Object.keys(value)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => value[k]);
}

export function orderedTurns(chat: AssistantChat | null): TurnView[] {
    if (!chat?.turns) return [];
    return Object.keys(chat.turns)
        .sort()
        .map((key) => ({ ...(chat.turns as Record<string, AssistantTurn>)[key], key }));
}

/** Same `YYYY-MM-DD hh:mm:ss` format as `startedAt`, so the strings compare. */
export function pendingView(chat: AssistantChat | null, now: Date): PendingView {
    const pending = chat?.pending;
    if (!pending) return { state: 'none' };
    const staleBefore = formatDateForStorage(new Date(now.getTime() - ASSISTANT_TURN_TIMEOUT_SECONDS * 1000));
    if (pending.startedAt <= staleBefore) return { state: 'failed', question: pending.question };
    return {
        state: 'running',
        question: pending.question,
        steps: toOrderedArray(pending.steps),
        text: pending.text ?? '',
    };
}

export function toolCalls(turn: AssistantTurn): AssistantToolCall[] {
    return toOrderedArray(turn.diagnostics?.toolCalls);
}

/** Newest first. */
export function chatListItems(chats: Record<string, AssistantChat> | null): ChatListItem[] {
    if (!chats) return [];
    return Object.entries(chats)
        .filter(([, chat]) => chat && typeof chat.title === 'string')
        .map(([key, chat]) => ({ key, title: chat.title, updatedAt: chat.updatedAt ?? '', contextBuildingKey: chat.contextBuildingKey }))
        .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

/** The building open on the current route, or undefined outside `/building/:id/...`. */
export function buildingKeyFromPath(pathname: string): string | undefined {
    const match = /^\/building\/([^/]+)/.exec(pathname);
    return match ? decodeURIComponent(match[1]) : undefined;
}
